/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React from "react";
import { Redirect } from "react-router-dom";

import { isEventDashboardPermissionError } from "../eventDashboardPermissionError";
import { AdminDashboardEventMenu } from "./AdminDashboardEventMenu";
import { useGetDashboardEventRoles } from "./useGetDashboardEventRoles";
import { useGetDashboardEventTicketIds } from "./useGetDashboardEventTicketIds";
import { useGetDashboardEvents } from "./useGetDashboardEvents";

import type {
  DashboardEventRole,
  DashboardRole,
} from "./useGetDashboardEventRoles";
import type { DashboardEventTicketIds } from "./useGetDashboardEventTicketIds";
import type { DashboardEvent } from "./useGetDashboardEvents";

type AdminDashboardEvent = {
  eventId: string;
  eventName: string;
  roles: DashboardRole[];
  ticketIds: string[];
};

const toAdminDashboardEvents = (
  eventRoles: readonly DashboardEventRole[],
  events: readonly DashboardEvent[],
  eventTicketIds: readonly DashboardEventTicketIds[]
): AdminDashboardEvent[] => {
  const ticketIdsByEventId = new Map(
    eventTicketIds.map(({ eventId, ticketIds }) => [eventId, ticketIds])
  );

  return [...events]
    .sort(
      (previousEvent, currentEvent) =>
        currentEvent.eventDateSeconds - previousEvent.eventDateSeconds
    )
    .flatMap(({ eventId, eventName }) => {
      const eventRole = eventRoles.find(
        (eventRole) => eventRole.eventId === eventId
      );

      if (!eventRole) {
        return [];
      }

      return [
        {
          eventId,
          eventName,
          roles: eventRole.roles,
          ticketIds: ticketIdsByEventId.get(eventId) ?? [],
        },
      ];
    });
};

export const AdminDashboard: React.FC = () => {
  const {
    eventRoles,
    isLoading: isLoadingEventRoles,
    isValidating: isValidatingEventRoles,
    error: eventRolesError,
  } = useGetDashboardEventRoles();
  const eventIds = React.useMemo(
    () => eventRoles.map(({ eventId }) => eventId),
    [eventRoles]
  );
  const {
    events,
    isLoading: isLoadingEvents,
    isValidating: isValidatingEvents,
    error: eventsError,
  } = useGetDashboardEvents(eventIds);
  const ticketDashboardEventIds = React.useMemo(
    () =>
      eventRoles.flatMap(({ eventId, roles }) => {
        return roles.includes("ticket_dashboard_viewer") ? [eventId] : [];
      }),
    [eventRoles]
  );
  const {
    eventTicketIds,
    isLoading: isLoadingEventTicketIds,
    isValidating: isValidatingEventTicketIds,
    error: eventTicketIdsError,
  } = useGetDashboardEventTicketIds(ticketDashboardEventIds);
  const dashboardEvents = React.useMemo(
    () => toAdminDashboardEvents(eventRoles, events, eventTicketIds),
    [eventRoles, eventTicketIds, events]
  );

  if (isEventDashboardPermissionError(eventRolesError)) {
    return <Redirect to="/" />;
  }

  if (eventRolesError ?? eventsError ?? eventTicketIdsError) {
    return <p>イベント一覧の取得に失敗しました。</p>;
  }

  if (isLoadingEventRoles || isLoadingEvents || isLoadingEventTicketIds) {
    return <p>読み込み中...</p>;
  }

  if (dashboardEvents.length === 0) {
    return <p>表示できるイベントはありません。</p>;
  }

  return (
    <section
      aria-busy={
        isValidatingEventRoles ||
        isValidatingEvents ||
        isValidatingEventTicketIds
      }
      css={styles.container}
    >
      <h1 css={styles.title}>イベント一覧</h1>
      <ul css={styles.eventList}>
        {dashboardEvents.map(({ eventId, eventName, roles, ticketIds }) => (
          <li key={eventId} css={styles.eventListItem}>
            <span css={styles.eventName}>{eventName}</span>
            <AdminDashboardEventMenu
              eventId={eventId}
              eventName={eventName}
              roles={roles}
              ticketIds={ticketIds}
            />
          </li>
        ))}
      </ul>
    </section>
  );
};

const styles = {
  container: css`
    box-sizing: border-box;
    width: 100%;
    max-width: 960px;
    padding: 48px 24px;
    margin: 0 auto;
  `,
  title: css`
    margin: 0;
    font-size: 24px;
    line-height: 1.5;
  `,
  eventList: css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0;
    margin: 24px 0 0;
    list-style: none;
  `,
  eventListItem: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 64px;
    padding: 8px 12px 8px 16px;
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
  `,
  eventName: css`
    min-width: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
    overflow-wrap: anywhere;
  `,
};
